// @ts-strict-ignore
/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { environment } from '../../environments/environment';
import {
  Pool,
  PoolPreference,
  PoolRateAndSide,
  RemainingCreditPayload,
  EntityPreferenceUpdatePayload,
  RollingPeriod,
  RollingPeriodList,
  EntityPreference,
  PoolStatusNotification,
  PoolPreferenceString,
  PoolPreferenceUpdatePayload,
  PoolMinimum,
  PoolStatus,
} from '../models/pool.model';
import { Benchmark } from '../models/rates.model';
import { Observable, Subject } from 'rxjs';
import {
  AngularFirestoreDocument,
  AngularFirestoreCollection,
  AngularFirestore,
} from '@angular/fire/compat/firestore';
import { map } from 'rxjs/operators';
import { PoolSummary, Tenor } from '../models/roll-dates.model';
import { formatDate } from '@angular/common';
@Injectable({
  providedIn: 'root',
})
export class PoolService {
  public participatingPools = new Subject<boolean>();
  public poolGrid = new Subject<any>();

  setUpPoolGrid(pools: Pool[]) {
    this.poolGrid.next(pools);
  }
  /**
   * @summary
   * Pools is cached locally but is currently not shared with any page.
   * Only the forward points and spot rate is currently used from the cached pools
   */
  private pools: Pool[];
  public tenors: Tenor[] = [];
  public benchmarks: Benchmark[] = [];

  public poolDocument: AngularFirestoreDocument<Pool[]>;
  public poolItems: Observable<Pool[]>;
  public poolNotificationItems: Observable<PoolStatusNotification[]>;
  public poolNotificationCollection: AngularFirestoreCollection<
    PoolStatusNotification[]
  >;
  public onTenorsLoaded = new Subject();

  private localTimeZone: string =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  constructor(
    private readonly api: ApiService,
    public afs: AngularFirestore,
    private readonly apiService: ApiService,
  ) {}
  /**
   * As of 12/28/2020
   *
   * @summary
   * Get Pools returns all data related between the member and that currency pair
   * @param1 Parameters is never used and can be deprecated
   */
  public getPools(
    entityId: string,
    parameters?: Record<string, string>,
  ): Observable<Pool[]> {
    return this.api.get(
      `pool/pools-summary?entityId=${entityId}`,
      parameters,
      environment.backendServer,
    );
  }

  /**
   * As of 05/25/2021
   *
   * @summary
   * Get Pool Minimum returns all data related between the member and their pool minimum
   * @param1 Parameters is never used and can be deprecated
   */
  public getEntityPreferences(
    entityId: string,
  ): Observable<EntityPreference[]> {
    return this.api.get(
      `pool/preferences?entityId=${entityId}`,
      {},
      environment.backendServer,
    );
  }

  /**
   * As of 12/28/2020
   *
   * @summary
   * Set Pools caches pool values in memory
   * @param1 pools all pools for the current member
   */
  public setPools(pools: Pool[]): void {
    this.pools = pools;
  }

  /**
   * As of 12/28/2020
   *
   * @summary
   * Finds remaining credit allowed to be used in provided pool
   * @param1 pool is the poolId that is being requested
   * @param2 shortCurrency is the currency being shorted in the provided pool
   */
  public async findRemainingCredit(
    pool: PoolSummary,
    editInput: any,
  ): Promise<RemainingCreditPayload> {
    const body = {
      poolId: pool.id,
      currencyPair: pool.currencyPair,
      dealtCurrency: editInput.hedgeCurrency,
      tenorIdentifier: pool.tenor.identifier,
      memberId: sessionStorage.getItem('selectedEntityId'),
      allocatedTotal:
        editInput.myAllocatedTotal.length === 0
          ? 0
          : editInput.myAllocatedTotal,
    };
    return await this.api.postAwait(
      `credit-validator/remaining-credit`,
      body,
      {},
      environment.backendServer,
    );
  }

  /**
   * As of 05/25/2021
   *
   * @summary
   * Updates the members pool minimums
   */
  public updatePoolMinimums(
    poolPreference: PoolMinimum[],
  ): Observable<EntityPreference[]> {
    return this.api.put(
      `pool/minimums`,
      poolPreference,
      {},
      environment.backendServer,
    );
  }

  /**
   * As of 05/25/2021
   *
   * @summary
   * Updates the members pool preference
   */
  public updateEntityPreferences(
    poolPreference: EntityPreferenceUpdatePayload[],
  ): Observable<EntityPreference[]> {
    return this.api.put(
      `pool/dealt-preferences`,
      poolPreference,
      {},
      environment.backendServer,
    );
  }

  /**
   * As of 01/18/2023
   *
   * @summary
   * Updates the members pool preference in the pools page
   */
  public updatePoolDealtPreferences(
    poolPreference: PoolPreferenceUpdatePayload,
  ): Observable<EntityPreference[]> {
    return this.api.put(
      `pool/dealt-preference`,
      poolPreference,
      {},
      environment.backendServer,
    );
  }

  /**
   * As of 12/28/2020
   *
   * @summary
   * Helper function to convert allocated credit amount to USD if currency pair being allocated does not include USD
   * This is where the cached pools value in the service is being used
   * @param1 currency that needs to be converted to USD
   */
  public getRatesAndSideForUSD(currency: string): PoolRateAndSide {
    for (const pool of this.pools) {
      if (
        pool.pool.currencyPair.indexOf(currency) > -1 &&
        pool.pool.currencyPair.indexOf('USD') > -1
      ) {
        return {
          rate: pool.rate.spotRate + pool.rate.forwardPoints,
          side:
            pool.pool.currencyPair.indexOf(currency) === 0 ? 'Base' : 'Quote',
        };
      }
    }
    return null;
  }

  /**
   * As of 12/28/2020
   *
   * @summary
   * Returns the dealt currency for the pool provided
   * @param2 pool
   */
  public getDealtCurrency(pool: Pool): string {
    if (
      (pool.memberResidualSide === 'Quote' && pool.nettingPercentage < 1) ||
      (pool.memberResidualSide === 'Base' && pool.nettingPercentage === 1)
    ) {
      return pool.pool.currencyPair.substring(0, 3);
    }

    return pool.pool.currencyPair.substring(3);
  }

  /**
   * As of 12/28/2020
   *
   * @summary
   * Returns the preferred currency for the pool provided if it exists
   * @param2 pool
   */
  public getPreferredCurrency(pool: Pool): string {
    const currencyPair = pool.pool.currencyPair;
    return pool.poolPreference === PoolPreference.base
      ? currencyPair.substring(0, 3)
      : currencyPair.substring(3);
  }

  public getDealtPreferenceFromPool(
    dealtPreference: string,
    currencyPair: string,
  ): string {
    return dealtPreference === PoolPreferenceString.base
      ? currencyPair.substring(0, 3)
      : currencyPair.substring(3);
  }

  /**
   * As of 10/26/2022
   *
   * @summary
   * Maps the Pool's rolling period
   * @param1 rollingPeriod
   */
  public rollingPeriod = (
    rollingPeriod: string,
    tenorShortForm: boolean = false,
  ) => {
    switch (rollingPeriod) {
      case RollingPeriod.mm:
        return tenorShortForm ? 'MM' : '(Mid-month)';
      case RollingPeriod.me:
        return tenorShortForm ? 'ME' : '(Month-end)';
      case RollingPeriod.mmThreeMonths:
        return tenorShortForm ? 'MM-3M' : '(Mid-month 3M)';
      default:
        return '';
    }
  };

  /**
   * As of 06/16/2022
   *
   * @summary
   * Generate a Pool Rolling Period list to be used for dropdown menus and tabs
   */
  public generateRollingPeriodList(): RollingPeriodList[] {
    const list: RollingPeriodList[] = [];
    const rollingPeriodArr = Object.values({ ...RollingPeriod });
    rollingPeriodArr.forEach((tab: RollingPeriod) => {
      list.push({
        tenor: tab,
        label: this.rollingPeriod(tab, false).slice(1, -1),
      });
    });
    return list;
  }

  public setAllAvailableTenors(tenors: Tenor[]): void {
    this.tenors = tenors;
    this.onTenorsLoaded.next(0);
  }

  public getAllAvailableTenors(): Tenor[] {
    return this.tenors;
  }

  public setAllAvailableBenchmarks(benchmarks: Benchmark[]): void {
    this.benchmarks = benchmarks;
  }

  public getAllAvailableBenchmarks(): Benchmark[] {
    return this.benchmarks;
  }

  /**
   * As of 11/04/2022
   *
   * @summary
   * Fatches the Pool Status Notification Collection from Firestore
   */
  public fetchPoolStatusNotificationCollection(): Observable<
    PoolStatusNotification[]
  > {
    this.poolNotificationCollection = this.afs.collection<any>(
      'poolStatusNotification',
    );
    return (this.poolNotificationItems = this.poolNotificationCollection
      .stateChanges(['added', 'modified'])
      .pipe(
        map((actions: []) =>
          actions.map(
            (a: { payload: { doc: { id: string; data: () => {} } } }) => {
              const data = a.payload.doc.data() as any;
              const id = a.payload.doc.id;
              return { id, ...data };
            },
          ),
        ),
      ));
  }

  /**
   * As of 11/04/2022
   *
   * @summary
   * Fatches the Pool positions collection from Firestore
   * @param1 selectedEntityId
   */
  public fetchPoolsCollection(selectedEntityId: string): Observable<Pool[]> {
    this.poolDocument = this.afs.doc(`pool/${selectedEntityId}`);
    return (this.poolItems = (this.poolDocument.collection('positions') as any)
      .stateChanges(['added', 'modified'])
      .pipe(
        map((actions: []) =>
          actions.map(
            (a: { payload: { doc: { id: string; data: () => {} } } }) => {
              const data = a.payload.doc.data() as Pool[];
              const id = a.payload.doc.id;
              return { id, ...data };
            },
          ),
        ),
      ));
  }

  public getPoolTenor(tenorId: string): Tenor {
    switch (tenorId) {
      case 'ONE_M':
        return {
          shortName: 'ME 1M',
          displayLabel: 'Month End - 1M',
          identifier: 'MONTH_END_1M',
        };
      case 'MM_1':
        return {
          shortName: 'MM 1M',
          displayLabel: 'Mid Month - 1M',
          identifier: 'MID_MONTH_1M',
        };
      case 'MM_3M':
        return {
          shortName: 'MM 3M',
          displayLabel: 'Mid Month - 3M',
          identifier: 'MID_MONTH_3M',
        };
      case 'D_SW':
        return {
          shortName: 'D SW',
          displayLabel: 'Daily - SW',
          identifier: 'DAILY_SW',
        };
      case 'D_1M':
        return {
          shortName: 'D 1M',
          displayLabel: 'Daily - 1M',
          identifier: 'DAILY_1M',
        };
      case 'D_3M':
        return {
          shortName: 'D 3M',
          displayLabel: 'Daily - 3M',
          identifier: 'DAILY_3M',
        };
    }

    return null;
  }
  /**
   * As of 04/04/2023
   *
   * @summary
   * Fatches the Pool oms orders
   * @param1 selectedEntityId
   */
  public async getPoolOrders(entityId: string, dateId: string): Promise<any[]> {
    return await this.apiService.getAwait(
      `pool/pool-order-allocations?entityId=${entityId}&dateId=${dateId}`,
      null,
      environment.backendServer,
    );
  }

  public formatValueDates(
    rollDate: string,
    nearLeg: string,
    farLeg: string,
  ): string {
    let rowValue: string;
    if (rollDate) {
      rowValue = `${formatDate(
        rollDate,
        'EE MMM d',
        'en-US',
        this.localTimeZone,
      )} \u2022\ ${formatDate(
        nearLeg,
        'MMM d',
        'en-US',
        this.localTimeZone,
      )} \u2794\ ${formatDate(farLeg, 'MMM d', 'en-US', this.localTimeZone)}`;
    } else {
      rowValue = `${formatDate(
        nearLeg,
        'MMM d',
        'en-US',
        this.localTimeZone,
      )} \u2794\ ${formatDate(farLeg, 'MMM d', 'en-US', this.localTimeZone)}`;
    }
    return rowValue;
  }

  public canViewDailyMatch(
    tenor: string,
    status: string,
    canViewDailyMatch: boolean,
    isFxhpAdmin: boolean,
  ): boolean {
    if (
      (tenor === 'DAILY_SW' || tenor === 'DAILY_1M' || tenor === 'DAILY_3M') &&
      (status === PoolStatus.open || status === PoolStatus.countdown) &&
      !canViewDailyMatch &&
      !isFxhpAdmin
    ) {
      return false;
    } else {
      return true;
    }
  }
  public shortNumFormat(value: string | number): { abs: number; key: string } {
    let abs = Math.abs(+value);
    const rounder = Math.pow(10, 1);

    let key = '';

    const powers = [
      { key: 'Q', value: Math.pow(10, 15) },
      { key: 'T', value: Math.pow(10, 12) },
      { key: 'B', value: Math.pow(10, 9) },
      { key: 'M', value: Math.pow(10, 6) },
      { key: 'K', value: 1000 },
    ];

    for (let i = 0; i < powers.length; i++) {
      let reduced = abs / powers[i].value;
      reduced = Math.round(reduced * rounder) / rounder;
      if (reduced >= 1) {
        abs = reduced;
        key = powers[i].key;
        break;
      }
    }
    return { abs, key };
  }
}
